import * as Actions from '../actions/leadActions';
import Lead from '../../entities/lead';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type LeadAction = { 
    type: string; 
    lead: Lead | null; 
    leadId: string | Lead; 
    partial: boolean; 
    lastFetch: number; 
    leads: Lead[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Lead> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Lead[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Lead> | null, firmId: string, userId: string, updates?: Lead[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'lead', 'leads', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Lead>} data
 * @param {Lead[]} [updates]
 * @returns {ArrayMap<Lead>}
 */
function updateStorage(data: ArrayMap<Lead>, updates?: Lead[]): ArrayMap<Lead> {
	if (logging) console.debug('⮂ leadReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Lead[]} [updates]
 * @returns {ArrayMap<Lead>}
 */
function updateStorageNoReturn(updates?: Lead[]): void {
	if (logging) console.debug('⮂ leadReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Lead[]} [data]
 * @returns {ArrayMap<Lead>}
 */
function createState(data?: Lead[] | ArrayMap<Lead>): ArrayMap<Lead> {
	if (!data) return ArrayMapRegistry.clone<Lead>('lead', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Lead>('lead', data.map(e => e));
    else return ArrayMapRegistry.clone<Lead>('lead', data);
}

function leads(state: ArrayMap<Lead>, action: LeadAction): ArrayMap<Lead> {
	if (!state) state = createState();

	let st: ArrayMap<Lead>;

	switch (action.type) {
		// action.lead - the new value of lead with id action.id
		// action.leadId - id of the target, used if new value is null
		case Actions.LEAD_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ leadReducer: FETCH_ONE_SUCCESS', action);

			if (action.lead === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.leadId)) {
				st = state.clone();
				st.set(action.lead);
				// state.clear();

				updateStorageNoReturn([action.lead]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.leads
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.leads
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.leads
			 !partial, !stateEmpty, fetchEmpty	-> D return leads in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge leads in state newer than those in action.leads
		 */
		case Actions.LEAD_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.leads.length === 0;
			const fetched = action.leads.map(lead => new Lead({...lead, persisted: true}));

			if (logging) console.debug(`⮂ leadReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} leads with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ leadReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ leadReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Lead> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Lead[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(lead => {
					const current = st.get(lead.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && lead.status === 'D') {
							// Remove it from state
							toUpdate.push(lead);
							st.delete(lead);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (lead.addedOn > current.addedOn) {
								toUpdate.push(lead);
								st.set(lead);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (lead.status !== 'D') {
							toUpdate.push(lead);
							st.set(lead);
						}
						else if (logging) console.debug('⮂ leadReducer: partialMerge: skipping deleted lead not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ leadReducer: empty: leads', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ leadReducer: fullMerge: leads', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localLead => {
                    // If this lead has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localLead.persisted) {
                        const fetchedLead = st.get(localLead.id);
                        if (!fetchedLead || localLead.addedOn > fetchedLead.addedOn) st.set(localLead);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.LEAD_FETCH_ONE_FAILURE:
			console.error('LEAD_FETCH_ONE_FAILURE', action.leadId, action.err);

			// No update to storage required
			return state;

		case Actions.LEAD_FETCH_FAILURE:
			console.error('LEAD_FETCH_FAILURE', action.leadId, action.err);

			// No update to storage required
			return state;

		case Actions.LEAD_RESTORE: {
		    let lead = new Lead({...action.lead});
            st = state.clone();

            const existing = st.get(lead.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (lead.addedOn >= existing.addedOn) {
                    st.set(lead);
                    return updateStorage(st, [lead]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (lead.status === 'D') return state;

                st.set(lead);
                return updateStorage(st, [lead]);
            }
        }
            
		case Actions.LEAD_SAVE: {
		    // console.debug('⮂ LEAD_SAVE reducer', action.lead?.id);
		    
			// clone the lead
			let lead = new Lead({...action.lead});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new lead in the state
			// If this is a create, add to the queue
			const _lead = st.get(lead.id);

			if (!_lead) st.set(lead);
			else {
				if (lead.addedOn >= _lead.addedOn) st.set(lead);
			}

			return updateStorage(st, [lead]);
		}

		case Actions.LEAD_SAVE_MANY: {
		    if (logging) console.debug('⮂ LEAD_SAVE_MANY reducer', action.leads.map(lead => lead.id).join(','));
		    
			// clone the leads
			let toProcess = action.leads.map(lead => new Lead(lead));

			st = state.clone();
			// state.clear();

			const updates: Lead[] = [];

			toProcess.forEach(lead => {
				// Add or update the new lead in the state
				// If this is a create, add to the queue
				const existing = st.get(lead.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (lead.status !== 'D') {
						st.push(lead);
						updates.push(lead);
					}
					else if (logging) console.debug('⮂ LEAD_SAVE_MANY: skipping deleted lead not in state');
				}
				else {
					if (lead.addedOn >= existing.addedOn) {
						st.set(lead);
						updates.push(lead);
					}
					else if (logging) console.debug('⮂ Skipping, existing lead had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.LEAD_REMOVE: {
			// clone the lead
			const lead = new Lead({...action.lead});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(lead);

			return updateStorage(st, [lead]);
		}

		case Actions.LEAD_RESTORE_FAILURE:
		case Actions.LEAD_SAVE_FAILURE: {
		    console.warn('LEAD_SAVE_FAILURE reducer', action.lead?.id);
		    if (!action.lead?.id) {
				console.warn('LEAD_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const lead = ensure(Lead, action.lead);
			if (!lead) {
				console.error('Could not find lead after call to save');
				return state;
			}

			st = state.clone();

			const _leadRef = state.get(lead.id);

			if (_leadRef && (_leadRef.addedOn === lead.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _lead = new Lead({..._leadRef});
				_lead.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_lead.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('lead', _lead).then(r => {
						if (r.isErr()) console.error(`⮂ leadReducer: failed to save lead with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ leadReducer: saved lead with fatalError info');
					}).catch(err => console.error(`⮂ leadReducer: failed to save lead with fatalError info: ${err.message}`));

				st.set(_lead);
			}

			return updateStorage(st);
		}

		case Actions.LEAD_SAVE_MANY_FAILURE: {
		    console.warn('LEAD_SAVE_MANY_FAILURE reducer', action.leads.map(lead => lead.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.LEAD_RESTORE_SUCCESS:
		case Actions.LEAD_SAVE_SUCCESS: {
			if (!action.lead?.id) {
				console.warn('LEAD_SAVE_SUCCESS reducer');
                return state;
			}

			const leadRef = state.get(action.lead?.id);
			if (!leadRef) {
				console.warn('Could not find lead after call to save');
                return state;
			}

			st = state.clone();

			if (action.lead.addedOn >= leadRef.addedOn) {
				st.set(action.lead);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const lead = new Lead({...leadRef, persisted: true});
				st.set(lead);
			}

			return updateStorage(st, [action.lead]);
		}

		case Actions.LEAD_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ LEAD_SAVE_MANY_SUCCESS reducer', action.leads.map(lead => lead.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const leads: Lead[] = [];

			action.leads.forEach(e => {
				const leadRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!leadRef) console.warn('Could not find lead after call to save');
				else if (e.addedOn >= leadRef.addedOn) {
				    st.set(e);
				    leads.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const lead = new Lead({...leadRef, persisted: true});
					st.set(lead);
					leads.push(lead);
				}
			});

			return updateStorage(st, leads);
		}

		case Actions.LEAD_REMOVE_FAILURE: {
		    console.warn('LEAD_REMOVE_FAILURE action', action.lead?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.LEAD_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.LEAD_RESTORE_MANY: {
			if (logging) console.debug('⮂ LEAD_RESTORE_MANY reducer', action.leads.map(lead => lead.id).join(','));

			const toProcess = action.leads.map(lead => new Lead({...lead}));
			st = state.clone();

			const updates: Lead[] = [];

			toProcess.forEach(lead => {
				const existing = st.get(lead.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (lead.addedOn >= existing.addedOn) {
						st.set(lead);
						updates.push(lead);
					}
					else if (logging) console.debug('⮂ LEAD_RESTORE_MANY: skipping older version of lead');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (lead.status !== 'D') {
						st.set(lead);
						updates.push(lead);
					}
					else if (logging) console.debug('⮂ LEAD_RESTORE_MANY: skipping deleted lead not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.LEAD_REMOVE_MANY: {
			if (logging) console.debug('⮂ LEAD_REMOVE_MANY reducer', action.leads.map(lead => lead.id).join(','));

			const toProcess = action.leads.map(lead => new Lead({...lead}));
			st = state.clone();

			toProcess.forEach(lead => {
				st.delete(lead);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default leads;